import { useCallback, useEffect, useMemo, useState } from "react";
import { ROLE_OPTIONS, ROLE_LABEL, isTaseronRole, normalizeRole, canSgk } from "../../lib/roles";
import UserPermissions from "../../components/UserPermissions";
import ActiveSessions from "../../components/ActiveSessions";
import CheckList from "../../components/CheckList";
import { KeyRound } from "lucide-react";
import { http, formatApiError } from "../../lib/api";
import { toast } from "../../lib/notify";
import { Pencil, Trash2, Plus, Save, ShieldCheck, User, HardHat, Lock, Unlock } from "lucide-react";
import { ConfirmDelete } from "../../components/ConfirmDelete";
import { FilterTh, useColFilters } from "../../components/ColFilters";

const USER_COLS = [
  { key: "name", label: "Ad Soyad", type: "text" },
  { key: "email", label: "E-posta", type: "text" },
  { key: "phone", label: "Telefon", type: "text" },
  { key: "role", label: "Yetki", options: [{ value: "admin", label: "Yönetici" }, { value: "data_entry", label: "Veri Girişi / Proje" }] },
  { key: "projects", label: "Sorumlu Proje", type: "multiany", value: (u) => (u.project_names?.length ? u.project_names : ["—"]) },
  { key: "subcontractor", label: "Bağlı Taşeron", value: (u) => u.subcontractor_name || "—" },
  { key: "created", label: "Kayıt Tarihi", type: "date", value: (u) => (u.created_at || "").slice(0, 10) },
];

const cls = "w-full px-3 py-2 text-sm border border-slate-300 rounded-md bg-white focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none";
const emptyUser = { name: "", email: "", password: "", role: "data_entry", assigned_card_ids: [], tc_no: "", phone: "", project_ids: [], location_ids: [], subcontractor_id: null, permissions: {}, can_sgk: false };
const NO_IDS = [];
const roleOptions = ROLE_OPTIONS;
const trName = (a, b) => (a.name || "").localeCompare(b.name || "", "tr");
const SCOPED_ROLES = ["gm_yetkili", "proje_koordinatoru", "taseron", "taseron_kullanici", "sube_yetkilisi", "data_entry"];
// rol seçenekleri: üst roller optgroup, alt roller "↳" ile — statik liste, bir kez hesaplanır
const ROLE_SELECT_OPTIONS = roleOptions.filter((o) => !o.parent).map((o) => {
  const subs = roleOptions.filter((x) => x.parent === o.value);
  if (!subs.length) return <option key={o.value} value={o.value}>{o.label}</option>;
  return <optgroup key={o.value} label={o.label}><option value={o.value}>{o.label}</option>{subs.map((sx) => <option key={sx.value} value={sx.value}>{`↳ ${sx.label}`}</option>)}</optgroup>;
});
const byBranchCode = (a, b) => (parseInt(a.branch_code) || 999999) - (parseInt(b.branch_code) || 999999) || (a.name || "").localeCompare(b.name || "", "tr");

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(emptyUser);
  const [editing, setEditing] = useState(null); // "new" | id
  const [delUser, setDelUser] = useState(null);
  const [regCards, setRegCards] = useState([]);
  const [permUser, setPermUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [locations, setLocations] = useState([]);
  useEffect(() => { http.get("/projects").then((r) => setProjects(r.data)).catch(() => {}); http.get("/locations").then((r) => setLocations(r.data)).catch(() => {}); }, []);
  useEffect(() => { http.get("/registered-cards").then((r) => setRegCards(r.data)).catch(() => {}); }, []);
  const [caris, setCaris] = useState([]);
  useEffect(() => { http.get("/caris", { params: { taseron: 1 } }).then((r) => setCaris(r.data.sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr")))).catch(() => {}); }, []);

  const toggleCard = (id) => setForm((f) => {
    const cur = f.assigned_card_ids || [];
    return { ...f, assigned_card_ids: cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id] };
  });

  const load = useCallback(() => http.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(formatApiError(e))), []);
  useEffect(() => { load(); }, [load]);
  const usersView = useMemo(() => {
    const cariName = Object.fromEntries(caris.map((c) => [c.id, c.name]));
    const projName = Object.fromEntries(projects.map((p) => [p.id, p.name]));
    const locProj = Object.fromEntries(locations.map((l) => [l.id, l.project_id]));
    return users.map((u) => {
      // sorumlu projeler: doğrudan atananlar + sorumlu şubelerin projeleri (şube yetkilisi)
      const ids = [...new Set([...(u.project_ids || []), ...(u.location_ids || []).map((id) => locProj[id]).filter(Boolean)])];
      return { ...u, subcontractor_name: u.subcontractor_id ? cariName[u.subcontractor_id] || "" : "", project_names: ids.map((id) => projName[id]).filter(Boolean).sort((a, b) => a.localeCompare(b, "tr")) };
    });
  }, [users, caris, projects, locations]);
  const [hf, setHf, visRows, autoOpts] = useColFilters(usersView, USER_COLS);
  const projectOpts = useMemo(() => [...projects].sort(trName).map((p) => ({ value: p.id, label: p.name, keys: [p.name] })), [projects]);
  const projIds = form.project_ids || NO_IDS;
  const locationOpts = useMemo(() => {
    const ids = form.project_ids || [];
    return locations
      .filter((l) => !ids.length || ids.includes(l.project_id))
      .sort(byBranchCode)
      .map((l) => ({ value: l.id, label: [l.branch_code, l.name, l.city].filter(Boolean).join(" - "), keys: [l.branch_code, l.name] }));
  }, [locations, form.project_ids]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing === "new") {
        await http.post("/users", form);
        toast.success("Kullanıcı oluşturuldu");
      } else {
        await http.put(`/users/${editing}`, form);
        toast.success(form.password ? "Kullanıcı güncellendi, şifre sıfırlandı" : "Kullanıcı güncellendi");
      }
      setEditing(null); setForm(emptyUser); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const unlock = async (u) => {
    try { await http.post(`/users/${u.id}/unlock`); toast.success(`${u.name} için giriş kilidi kaldırıldı`); load(); } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeUser = async () => {
    if (!delUser) return;
    try {
      await http.delete(`/users/${delUser.id}`);
      toast.success(`"${delUser.name}" silindi`);
      setDelUser(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-6" data-testid="page-users">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Kullanıcılar</h1>
          <p className="text-sm text-slate-500 mt-1">Sisteme giriş yapabilecek kullanıcıları buradan oluşturun ve yetki (rol) atayın.</p>
        </div>
        <button onClick={() => { setForm(emptyUser); setEditing("new"); }} data-testid="user-add"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800">
          <Plus className="w-3.5 h-3.5" /> Yeni Kullanıcı
        </button>
      </div>

      <ActiveSessions />

      {editing && (
        <form onSubmit={submit} className="bg-white border border-slate-200 rounded-lg p-4 flex items-end gap-3 flex-wrap" data-testid="user-form">
          <L label="Ad Soyad"><input required data-testid="user-field-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={cls} placeholder="örn. Ayşe Yılmaz" /></L>
          <L label="E-posta"><input required type="email" data-testid="user-field-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={cls} placeholder="ornek@firma.com" /></L>
          <L label="T.C. Kimlik No"><input data-testid="user-field-tc" maxLength={11} value={form.tc_no || ""} onChange={(e) => setForm({ ...form, tc_no: e.target.value.replace(/\D/g, "") })} className={cls} placeholder="11 hane" /></L>
          <L label="Telefon"><input data-testid="user-field-phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={cls} placeholder="05xx xxx xx xx" /></L>
          <L label={editing === "new" ? "Geçici Şifre (ilk girişte değiştirilir)" : "Yeni Şifre (boş = değişmez)"}>
            <input type="password" required={editing === "new"} minLength={6} data-testid="user-field-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={cls} placeholder="en az 6 karakter" />
          </L>
          <L label="Yetki (Rol)">
            <select data-testid="user-field-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={cls}>
              {ROLE_SELECT_OPTIONS}
            </select>
          </L>
          {form.role === "ik" && (
            <label className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border text-xs cursor-pointer self-end ${form.can_sgk ? "border-emerald-500 bg-emerald-50 text-emerald-900 font-semibold" : "border-slate-300 text-slate-600 hover:border-emerald-400"}`} data-testid="user-field-can-sgk" title="İşaretli İK kullanıcıları personel kartından SGK e-İşe Giriş / e-İşten Çıkış yapabilir ve SGK Excel/eklentisini kullanabilir">
              <input type="checkbox" checked={!!form.can_sgk} onChange={(e) => setForm({ ...form, can_sgk: e.target.checked })} className="accent-emerald-600 w-3.5 h-3.5" />
              <ShieldCheck className="w-3.5 h-3.5" /> SGK İşe Giriş / İşten Çıkış yapabilir
            </label>
          )}
          {isTaseronRole(form.role) && (
            <L label="Bağlı Taşeron (Cari)">
              <select required data-testid="user-field-subcontractor" value={form.subcontractor_id || ""} onChange={(e) => setForm({ ...form, subcontractor_id: e.target.value || null })} className={cls}>
                <option value="">Seçiniz…</option>
                {caris.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              {form.role === "taseron_kullanici" && <span className="block text-[11px] text-slate-500 mt-1">Taşeron kullanıcısı yalnız bu taşeronun iş emirlerini görür ve tamamlar</span>}
            </L>
          )}
          {SCOPED_ROLES.includes(form.role) && (
            <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="user-scope">
              <L label="Sorumlu Projeler (çoklu)">
                <CheckList testid="user-field-projects" placeholder="Proje adı ile ara…" value={projIds} onChange={(v) => setForm({ ...form, project_ids: v })} options={projectOpts} excelTitle="Sorumlu Projeler" />
                {form.role === "proje_koordinatoru" && <span className="block text-[11px] text-slate-500 mt-1">Proje koordinatörü birden çok projeye atanabilir; yalnız bu projeleri ve bu projelerdeki kayıt, iş emri, onay ve hakedişleri görür</span>}
              </L>
              <L label={form.role === "sube_yetkilisi" ? "Sorumlu Şube(ler)" : "Şube kısıtı (boş = projedeki tüm şubeler)"}>
                <CheckList testid="user-field-locations" placeholder="Şube kodu, adı veya il ile ara…" emptyText={projIds.length ? "Seçili projelerde şube yok" : "Önce proje seçin veya arayın"} value={form.location_ids || []} onChange={(v) => setForm({ ...form, location_ids: v })}
                  options={locationOpts} excelTitle="Sorumlu Subeler" />
              </L>
            </div>
          )}
          <div className="w-full">
            <span className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide">Atanan Kredi Kartları <span className="normal-case font-normal text-slate-400">(yalnızca bu kartları görür ve ekstre girişi yapabilir; boşsa kredi kartı bölümü gizlenir)</span></span>
            <div className="flex flex-wrap gap-2" data-testid="user-card-assign">
              {regCards.map((rc) => (
                <label key={rc.id} className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs cursor-pointer transition-colors ${(form.assigned_card_ids || []).includes(rc.id) ? "border-amber-500 bg-amber-50 text-amber-900 font-semibold" : "border-slate-300 text-slate-600 hover:border-amber-400"}`} data-testid={`user-card-opt-${rc.id}`}>
                  <input type="checkbox" checked={(form.assigned_card_ids || []).includes(rc.id)} onChange={() => toggleCard(rc.id)} className="accent-amber-600 w-3.5 h-3.5" />
                  {rc.bank} — {rc.card_no}{rc.holder_name ? ` (${rc.holder_name})` : ""}
                </label>
              ))}
              {regCards.length === 0 && <span className="text-[11px] text-slate-400">Kayıtlı kart yok — önce Veri Girişi → Kredi Kartları bölümünden kart tanımlayın</span>}
            </div>
          </div>
          <div className="flex gap-2">
            <button type="submit" data-testid="user-save" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800"><Save className="w-3.5 h-3.5" /> Kaydet</button>
            <button type="button" onClick={() => { setEditing(null); setForm(emptyUser); }} data-testid="user-cancel" className="px-3 py-2 text-xs text-slate-600 hover:text-slate-900">İptal</button>
          </div>
        </form>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full tbl">
          <thead className="bg-slate-50">
            <tr>
              {USER_COLS.map((c) => (
                <th key={c.key} className="px-3 py-2 text-[10px] uppercase tracking-wider text-slate-600 font-semibold text-left">
                  <FilterTh col={c} hf={hf} setHf={setHf} testid={`user-hf-${c.key}`} autoOptions={autoOpts[c.key]} />
                </th>
              ))}
              <th className="px-3 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {visRows.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`user-row-${u.id}`}>
                <td className="px-3 py-2 text-xs font-semibold text-slate-900">{u.name}</td>
                <td className="px-3 py-2 text-xs" data-no-upper>{u.email}</td>
                <td className="px-3 py-2 text-xs font-mono whitespace-nowrap" data-testid={`user-phone-${u.id}`}>{u.phone || <span className="text-slate-400">—</span>}</td>
                <td className="px-3 py-2 text-xs">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold ${u.role === "admin" ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`} data-testid={`user-role-${u.id}`}>{u.role === "admin" ? <ShieldCheck className="w-3 h-3" /> : <User className="w-3 h-3" />} {ROLE_LABEL[u.role] || u.role}</span>
                  {u.must_change_password && <span className="ml-1 text-[10px] text-amber-700" title="İlk girişte şifre değiştirecek">geçici şifre</span>}
                  {u.login_locked && <span className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-red-100 text-red-800 text-[10px] font-bold" title="5 başarısız denemeden sonra 15 dk giriş kilidi — Kilidi Aç ile hemen kaldırılır" data-testid={`user-locked-${u.id}`}><Lock className="w-3 h-3" /> KİLİTLİ</span>}
                  {canSgk(u) && <span className="ml-1 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px] font-bold" title="SGK e-İşe Giriş / e-İşten Çıkış yapabilir" data-testid={`user-sgk-${u.id}`}>SGK</span>}
                </td>
                <td className="px-3 py-2 text-xs" data-testid={`user-projects-${u.id}`}><ProjectsCell names={u.project_names} /></td>
                <td className="px-3 py-2 text-xs" data-testid={`user-subcontractor-${u.id}`}><SubcontractorCell u={u} /></td>
                <td className="px-3 py-2 text-xs text-slate-500">{u.created_at ? new Date(u.created_at).toLocaleDateString("tr-TR") : "—"}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {u.role !== "admin" && <button onClick={() => setPermUser(u)} data-testid={`user-perms-${u.id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-emerald-600 bg-emerald-50 text-[11px] font-bold text-emerald-800 hover:bg-emerald-100 mr-1.5"><KeyRound className="w-3.5 h-3.5" /> Yetkiler</button>}
                  {u.login_locked && <button onClick={() => unlock(u)} data-testid={`user-unlock-${u.id}`} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-red-600 bg-red-50 text-[11px] font-bold text-red-700 hover:bg-red-100 mr-1.5"><Unlock className="w-3.5 h-3.5" /> Kilidi Aç</button>}
                  <button onClick={() => { setForm({ ...emptyUser, ...u, role: normalizeRole(u.role), password: "", assigned_card_ids: u.assigned_card_ids || [] }); setEditing(u.id); }} data-testid={`user-edit-${u.id}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-slate-400 bg-white text-[11px] font-bold text-slate-700 hover:bg-slate-100 mr-1.5">
                    <Pencil className="w-3.5 h-3.5" /> Düzenle
                  </button>
                  <button onClick={() => setDelUser(u)} data-testid={`user-delete-${u.id}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-red-600 bg-red-50 text-[11px] font-bold text-red-700 hover:bg-red-100">
                    <Trash2 className="w-3.5 h-3.5" /> Sil
                  </button>
                </td>
              </tr>
            ))}
            {visRows.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-sm text-slate-500" data-testid="users-empty">{users.length === 0 ? "Kullanıcı yok" : "Filtrelerle eşleşen kullanıcı yok"}</td></tr>}
          </tbody>
        </table>
      </div>

      {permUser && <UserPermissions user={permUser} onClose={() => setPermUser(null)} onSaved={load} />}
      {delUser && (
        <ConfirmDelete testid="user-del" title={`Kullanıcı Sil: ${delUser.name}`}
          message={`"${delUser.name}" (${delUser.email}) kullanıcısı silinecek — bu kullanıcı bir daha sisteme giriş yapamayacak. Emin misiniz?`}
          step2Message={`Son onay: "${delUser.name}" kullanıcısını silmek istediğinize tekrar emin misiniz?`}
          onConfirm={removeUser} onCancel={() => setDelUser(null)} />
      )}
    </div>
  );
}

function ProjectsCell({ names = [] }) {
  if (!names.length) return <span className="text-slate-400">—</span>;
  return (
    <span className="flex flex-wrap gap-1" title={names.join(", ")}>
      {names.slice(0, 2).map((n) => <span key={n} className="px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-900 text-[10px] font-semibold truncate max-w-[160px]">{n}</span>)}
      {names.length > 2 && <span className="text-[10px] text-slate-500 self-center">+{names.length - 2}</span>}
    </span>
  );
}

function SubcontractorCell({ u }) {
  if (u.subcontractor_id) return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-sky-50 border border-sky-200 text-sky-900 text-[11px] font-semibold"><HardHat className="w-3 h-3" /> {u.subcontractor_name || "Taşeron bulunamadı"}</span>;
  if (isTaseronRole(u.role)) return <span className="text-[11px] text-red-700 font-semibold" title="Düzenle ile bağlı taşeronu seçin">taşeron atanmadı</span>;
  return <span className="text-slate-400">—</span>;
}

function L({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide">{label}</span>
      {children}
    </label>
  );
}
